import { createApp } from 'vue';

import App from './App.vue';
import router from './router';

import './assets/main.css';

import * as Sentry from '@sentry/vue';
import { captureException } from '@sentry/vue';
import { createHead } from '@unhead/vue/client';
import { createI18n } from 'vue-i18n';

import en from './i18n/en.json' with { type: 'json' };
import fr from './i18n/fr.json' with { type: 'json' };
import { getEnvironment, isSentryEnabled } from './lib/environment';

// get from query params first
const urlParams = new URLSearchParams(window.location.search);
const currentLocale = urlParams.get('locale') || localStorage.getItem('locale') || navigator.language.split('-')[0];
const i18n = createI18n({
  locale: currentLocale,
  fallbackLocale: 'en',
  availableLocales: ['en', 'fr'],
  warnHtmlMessage: false,
  messages: {
    en: en,
    fr: fr,
  },
});

// Set default session as soon as possible
const urlSessionId = urlParams.get('session');
if (!urlSessionId) {
  urlParams.set('session', `session-${Date.now()}`);
  window.location.search = urlParams.toString();
}

const app = createApp(App);

const head = createHead();
app.use(head);

Sentry.init({
  app,
  dsn: 'https://3e606b743dc096999b08f5cf4205aac8@sentry.365talents.com/7',
  environment: getEnvironment(),
  enabled: isSentryEnabled(), // Disable Sentry in local
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.httpClientIntegration(),
    Sentry.replayIntegration({
      networkDetailAllowUrls: [window.location.origin],
      minReplayDuration: 0,
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      mask: ['input[type="password"]'],
    }),
  ],
  // Tracing
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// used by the node backend with puppeteer to report errors to Sentry
// https://github.com/365talents/skaja-agent/pull/112
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).__SENTRY__.captureException = (message: string) => {
  captureException(new Error(message));
};

app.use(router);
app.use(i18n);

app.mount('#app');

// Hot updates translation files, must be string literal to be statically handled
if (import.meta.hot) {
  import.meta.hot.accept('/src/i18n/en.json', (mod) => {
    i18n.global.setLocaleMessage('en', mod!.default);
  });
  import.meta.hot.accept('/src/i18n/fr.json', (mod) => {
    i18n.global.setLocaleMessage('fr', mod!.default);
  });
}
